import React, { useState, useEffect, useRef } from 'react'
import NameTag from './nameTag'

const SOCKET_URL = process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:3000'

const Admin = () => {
  const socket = useRef()
  const [users, setUsers] = useState({})
  const [games, setGames] = useState({})
  const [events, setEvents] = useState([])
  const [gamePlays, setGamePlays] = useState({})

  const onUpdateName = async (userToken, alias) => {
    if (!socket.current) {
      alert('no socket')
      return
    }
    setUsers(users => ({
      ...users,
      [userToken]: {
        ...users[userToken],
        alias,
      }
    }))
    await socket.current.emit('add_alias', { userToken, alias })
  }

  useEffect(() => {
    if (!window.io) {
      return
    }
    socket.current = window.io(SOCKET_URL)
    const adminKey = localStorage.getItem('adminKey')
    socket.current.emit('admin_login', { adminKey })

    setUsers({})
    setGames({})
    setEvents([])
    setGamePlays({})

    socket.current.on('init', ({ events, activity, gamePlays, users: dbUsers, games: dbGames }) => {
      console.log({ events, activity, gamePlays, users: dbUsers, games: dbGames })
      setEvents(events)

      dbUsers.forEach(dbUser => {
        setUsers(users => ({
          ...users,
          [dbUser.user_token]: {
            ...(users[dbUser.user_token] || {}),
            ts: new Date(dbUser.last_active_at).getTime(),
            alias: dbUser.alias,
          },
        }))
      })

      dbGames.forEach(dbGame => {
        setGames(games => ({
          ...games,
          [dbGame.short_id]: {
            users: [],
            ...(games[dbGame.short_id] || {}),
            ts: new Date(dbGame.updated_at).getTime(),
          },
        }))

        ;(dbGame.users || []).forEach(({ userToken, plays }) => {
          setGames(games => ({
            ...games,
            [dbGame.short_id]: {
              ...games[dbGame.short_id],
              users: Array.from(new Set([...(games[dbGame.short_id].users || []), userToken])),
            },
          }))
          setGamePlays(gamePlays => ({
            ...gamePlays,
            [dbGame.short_id]: {
              ...gamePlays[dbGame.short_id],
              [userToken]: plays,
            },
          }))
        })
      })

      gamePlays.forEach(({ userId, gameId, plays }) => {
        setGamePlays(gamePlays => ({
          ...gamePlays,
          [gameId]: {
            ...gamePlays[gameId],
            [userId]: (gamePlays[gameId]?.[userId] || 0) + plays,
          }
        }))
      })

      const gameLives = {}
      activity.forEach(({ gameId, left }) => {
        gameLives[gameId] = gameLives[gameId] || !left
      })
      activity.forEach(({ userId, gameId, ts, left }) => {
        setUsers(users => ({
          ...users,
          [userId]: {
            ...(users[userId] || {}),
            gameId,
            ts,
            left,
          },
        }))
        setGames(games => ({
          ...games,
          [gameId]: { 
            users: Array.from(new Set([...(games[gameId]?.users || []), userId])),
            live: gameLives[gameId],
            ts,
          },
        }))
      })
    })

    socket.current.on('update', (message) => {
      if (message.type === 'user_activity') {
        const { userId, gameId, ts } = message
        setUsers(users => ({
          ...users,
          [userId]: {
            ...(users[userId] || {}),
            gameId,
            ts,
            left: null,
          },
        }))
        setGames(games => ({
          ...games,
          [gameId]: {
            users: Array.from(new Set([...(games[gameId]?.users || []), userId])),
            live: true,
            ts,
          },
        }))
      } else if (message.type === 'gameplay') {
        const { userId, gameId } = message
        setGamePlays(gamePlays => ({
          ...gamePlays,
          [gameId]: {
            ...gamePlays[gameId],
            [userId]: (gamePlays[gameId]?.[userId] || 0) + 1,
          },
        }))
      } else if (message.type === 'user_removed') {
        const { userId } = message
        setUsers(users => ({
          ...users,
          [userId]: { ...users[userId], left: Date.now() },
        }))
      } else if (message.type === 'event') {
        const { idType, id, event, ts } = message
        setEvents(events => {
          const newEvents = [...events]
          newEvents.unshift({ idType, id, event, ts })
          return newEvents
        })

        // hack for end game
        if (idType === 'game' && event === 'ended') {
          setGames(games => ({
            ...games,
            [id]: {
              ...games[id],
              live: false,
            },
          }))
        }
      }
    })

    return () => {
      socket.current.disconnect()
    }
  }, [window.io])

  return (
    <div className="flex w-full min-h-screen p-4" style={{ boxSizing: 'border-box' }}>
      <div className="w-full">
        <h1 className="text-3xl font-bold text-center mb-4">
          Admin
        </h1>
        <div className="flex gap-4 pb-4">
  
          {/* Left Sidebar */}
          <div className='w-1/4 px-4 rounded-lg' style={{ border: '1px solid black' }}>
            <h2 className="text-xl font-semibold mb-4 border-b pb-2">Recent Users</h2>
            {Object.entries(users)
              .sort((a, b) => b[1].ts - a[1].ts)
              .map(([userId, { gameId, ts, left, alias }]) => (
                <div key={userId} className="mb-4 p-2 bg-gray-100 rounded-lg">
                  <div className="font-medium text-gray-800">
                    <NameTag
                      alias={alias}
                      userToken={userId}
                      onUpdate={onUpdateName} />
                  </div>
                  <div className="text-sm text-gray-500">Status: {left || !gameId ? 'Offline' : 'Online'}</div>
                  {gameId && !left && (
                    <div className="text-sm text-gray-500">Game ID: {gameId}</div>
                  )}
                  <div className="text-sm text-gray-500">Last Active: {new Date(ts).toLocaleString()}</div>
                </div>
              ))}
          </div>
  
          {/* Main Content */}
          <div className="w-1/2 px-4 rounded-lg" style={{ border: '1px solid black' }}>
            <h2 className="text-xl font-semibold mb-4 border-b pb-2">Recent Games</h2>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="py-2 px-3 font-medium ">Online</th>
                  <th className="py-2 px-3 font-medium ">Game ID</th>
                  <th className="py-2 px-3 font-medium">Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(games)
                  .sort((a, b) => b[1].ts - a[1].ts)
                  .map(([gameId, { users: gameUsers, ts, live }]) => (
                    <React.Fragment key={gameId}>
                      <tr className="hover:bg-gray-50">
                        <td className="py-2 px-3">{live ? 'Yes' : 'No'}</td>
                        <td className="py-2 px-3">{gameId}</td>
                        <td className="py-2 px-3">{new Date(ts).toLocaleString()}</td>
                      </tr>
                      <tr>
                        <td colSpan="3" className="py-1">
                          <div className="ml-6">
                            {gameUsers.map(userId => (
                              <div key={`game-users-${userId}`} className="text-sm text-gray-600">
                                <NameTag
                                  alias={users[userId]?.alias}
                                  userToken={userId}
                                  className='text-sm'
                                  onUpdate={onUpdateName} />:&nbsp;
                                {gamePlays[gameId]?.[userId] || 0} plays
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
              </tbody>
            </table>
          </div>
  
          {/* Event Log */}
          <div className="w-1/4 px-4 rounded-lg" style={{ border: '1px solid black' }}>
            <h2 className="text-xl font-semibold mb-4 border-b pb-2">Event Log</h2>
            {events.map(({ idType, id, event, ts }, index) => {
              let title
              if (idType === 'user' && event == 'joined game') {
                title = 'User joined game'
              } else if (idType === 'game' && event == 'started') {
                title = 'Game started'
              } else if (idType === 'game' && event == 'ended') {
                title = 'Game ended'
              } else if (idType === 'user' && event == 'left game') {
                title = 'User left game'
              }
              return (
                <div key={index} className="mb-4 p-2 bg-gray-100 rounded-lg">
                  <div className="font-medium text-gray-800 mb-2">{title || event}</div>
                  <div className="text-sm text-gray-500 mb-1">
                    {idType === 'user' && (
                      <NameTag
                        userToken={id}
                        alias={users[id]?.alias}
                        onUpdate={onUpdateName}
                        className='text-sm' />
                    ) || (
                      <span>{id}</span>
                    )}
                  </div>
                  {/* <div className="text-sm text-gray-500">Event: {event}</div> */}
                  <div className="text-sm text-gray-500">{new Date(ts).toLocaleString()}</div>
                </div>
              )
            })}
          </div>
  
        </div>
      </div>
    </div>
  )
}

export default Admin
